/**
 * Shopify Places Autocomplete JavaScript Class
 * Compatible with Shopify themes and handles CORS properly
 * 
 * Usage:
 * 1. Add this script to your theme's assets folder
 * 2. Include it in your theme.liquid: {{ 'shopify-places-autocomplete.js' | asset_url | script_tag }}
 * 3. Use the ShopifyPlacesAutocomplete class
 */

class ShopifyPlacesAutocomplete {
    constructor(inputId, options = {}) {
        this.input = document.getElementById(inputId);
        if (!this.input) {
            console.error('Places autocomplete input not found:', inputId);
            return;
        }

        // Configuration
        this.apiBaseUrl = this.input.dataset.apiUrl || options.apiBaseUrl || 'https://python-demo-875152285307.northamerica-northeast2.run.app';
        this.apiKey = this.input.dataset.apiKey || options.apiKey || '';
        this.debounceTime = options.debounceTime || 300;
        this.minQueryLength = options.minQueryLength || 3;
        
        // Elements
        this.resultsContainer = document.getElementById(inputId.replace('input', 'results'));
        this.selectedPlace = null;
        
        // Initialize
        this.init();
    }
    
    init() {
        if (!this.resultsContainer) {
            console.error('Results container not found');
            return;
        }
        
        // Event listeners
        this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), this.debounceTime));
        this.input.addEventListener('blur', () => {
            // Delay hiding to allow clicks on results
            setTimeout(() => this.hideResults(), 200);
        });
        this.input.addEventListener('focus', () => {
            if (this.input.value.length >= this.minQueryLength) {
                this.handleInput({ target: this.input });
            }
        });
        
        // Keyboard navigation
        this.input.addEventListener('keydown', this.handleKeydown.bind(this));
        
        console.log('Shopify Places Autocomplete initialized for:', this.input.id);
    }
    
    async handleInput(event) {
        const query = event.target.value.trim();
        
        if (query.length < this.minQueryLength) {
            this.hideResults();
            return;
        }
        
        this.showLoading();
        
        try {
            const url = `${this.apiBaseUrl}/api/places/autocomplete?input=${encodeURIComponent(query)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Add API key if provided
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors' // Important for Shopify CORS
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success' && data.predictions) {
                this.showResults(data.predictions);
            } else {
                this.showError(data.error || 'No results found');
            }
            
        } catch (error) {
            console.error('Places API error:', error);
            this.showError('Failed to fetch places. Please try again.');
        }
    }
    
    showLoading() {
        this.resultsContainer.innerHTML = '<div class="signamic-loading places-loading">Finding businesses...</div>';
        this.resultsContainer.style.display = 'block';
    }
    
    showError(message) {
        this.resultsContainer.innerHTML = `<div class="signamic-error places-error">${message}</div>`;
        this.resultsContainer.style.display = 'block';
    }
    
    showResults(predictions) {
        this.resultsContainer.innerHTML = '';
        
        if (predictions.length === 0) {
            this.showError('No businesses found');
            return;
        }
        
        predictions.forEach((prediction, index) => {
            const item = document.createElement('div');
            item.className = 'signamic-search__result-item places-result-item';
            item.dataset.index = index;
            
            item.innerHTML = `
                <div class="signamic-result-content">
                    <div class="signamic-search__result-main places-main-text">${this.escapeHtml(prediction.main_text)}</div>
                    <div class="signamic-search__result-secondary places-secondary-text">${this.escapeHtml(prediction.secondary_text)}</div>
                </div>
            `;
            
            // Click handler
            item.addEventListener('click', () => {
                this.selectPlace(prediction);
            });
            
            // Mouse events for keyboard navigation
            item.addEventListener('mouseenter', () => {
                this.clearHighlight();
                item.classList.add('highlighted');
            });
            
            this.resultsContainer.appendChild(item);
        });
        
        this.resultsContainer.style.display = 'block';
    }
    
    hideResults() {
        this.resultsContainer.style.display = 'none';
        this.clearHighlight();
    }
    
    selectPlace(place) {
        this.input.value = place.description;
        this.selectedPlace = place;
        this.hideResults();
        
        // Trigger custom events for Shopify integration
        this.input.dispatchEvent(new CustomEvent('placeSelected', {
            detail: place,
            bubbles: true
        }));
        
        // Also trigger a generic change event
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        
        console.log('Selected place:', place);
    }
    
    // Keyboard navigation
    handleKeydown(event) {
        const results = this.resultsContainer.querySelectorAll('.signamic-search__result-item');
        const highlighted = this.resultsContainer.querySelector('.signamic-search__result-item.highlighted');
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (results.length > 0) {
                    const next = highlighted ? 
                        highlighted.nextElementSibling || results[0] : 
                        results[0];
                    this.highlight(next);
                }
                break;
                
            case 'ArrowUp':
                event.preventDefault();
                if (results.length > 0) {
                    const prev = highlighted ? 
                        highlighted.previousElementSibling || results[results.length - 1] : 
                        results[results.length - 1];
                    this.highlight(prev);
                }
                break;
                
            case 'Enter':
                event.preventDefault();
                if (highlighted) {
                    highlighted.click();
                }
                break;
                
            case 'Escape':
                this.hideResults();
                break;
        }
    }
    
    highlight(item) {
        this.clearHighlight();
        item.classList.add('highlighted');
        item.scrollIntoView({ block: 'nearest' });
    }
    
    clearHighlight() {
        const highlighted = this.resultsContainer.querySelector('.signamic-search__result-item.highlighted, .places-result-item.highlighted');
        if (highlighted) {
            highlighted.classList.remove('highlighted');
        }
    }
    

    
    // Get place details
    async getPlaceDetails(placeId) {
        try {
            const url = `${this.apiBaseUrl}/api/places/details?place_id=${encodeURIComponent(placeId)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data.status === 'success' ? data.place : null;
            
        } catch (error) {
            console.error('Place details error:', error);
            return null;
        }
    }
    
    // Get place reviews
    async getPlaceReviews(placeId) {
        try {
            const url = `${this.apiBaseUrl}/api/places/reviews?place_id=${encodeURIComponent(placeId)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            return data.status === 'success' ? data : null;
            
        } catch (error) {
            console.error('Place reviews error:', error);
            return null;
        }
    }
    
    // Generate sign HTML with dynamic reviews
    async generateSign(placeId) {
        try {
            const url = `${this.apiBaseUrl}/api/places/sign?place_id=${encodeURIComponent(placeId)}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (this.apiKey) {
                headers['x-api-key'] = this.apiKey;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success' && data.sign_html) {
                // Store dynamic reviews globally for the sign to use
                if (data.reviews && Array.isArray(data.reviews)) {
                    window.DYNAMIC_REVIEWS = data.reviews;
                    window.PLACE_ID = placeId;
                    console.log('Dynamic reviews loaded:', data.reviews);
                }
                return data.sign_html;
            }
            
            return null;
            
        } catch (error) {
            console.error('Sign generation error:', error);
            return null;
        }
    }
    
    // Utility functions
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
    
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    // Public methods for Shopify integration
    getSelectedPlace() {
        return this.selectedPlace;
    }
    
    clearSelection() {
        this.selectedPlace = null;
        this.input.value = '';
        this.hideResults();
    }
    
    setValue(value) {
        this.input.value = value;
    }
}

// Global namespace for Shopify
window.ShopifyPlacesAutocomplete = ShopifyPlacesAutocomplete;

// Auto-initialize if data attributes are present
document.addEventListener('DOMContentLoaded', function() {
    const inputs = document.querySelectorAll('input[data-api-url]');
    inputs.forEach(input => {
        if (input.id && !input.dataset.initialized) {
            new ShopifyPlacesAutocomplete(input.id);
            input.dataset.initialized = 'true';
        }
    });
});

// CSS for keyboard navigation (inject if not present)
const style = document.createElement('style');
style.textContent = `
    .signamic-search__result-item.highlighted,
    .places-result-item.highlighted {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color: white !important;
        transform: translateX(8px) scale(1.02) !important;
    }
    
    .signamic-search__result-item.highlighted .signamic-search__result-main,
    .signamic-search__result-item.highlighted .signamic-search__result-secondary,
    .places-result-item.highlighted .places-secondary-text {
        color: white !important;
    }
    
    .signamic-search__result-item.highlighted .signamic-search__result-secondary {
        opacity: 0.9 !important;
    }
`;
document.head.appendChild(style);


// Helper function to animate sign reviews
window.runSignReviewAnimation = function(options) {
    // options: { reviews, elementId, fadeDuration, intervalDuration }
    const reviews = options.reviews || window.DYNAMIC_REVIEWS || [];
    const elementId = options.elementId || ('review-text-' + (window.PLACE_ID || ''));
    const fadeDuration = options.fadeDuration || 1000; // ms
    const intervalDuration = options.intervalDuration || 4000; // ms
    setTimeout(function() {
        const reviewElement = document.getElementById(elementId);
        let currentIndex = 0;
        if (reviewElement && reviews.length > 0) {
            reviewElement.style.transition = 'opacity 1s ease-in-out';
            if (reviews.length > 1) {
                reviewElement.textContent = reviews[0];
                const intervalId = setInterval(function() {
                    currentIndex = (currentIndex + 1) % reviews.length;
                    reviewElement.style.opacity = '0';
                    setTimeout(function() {
                        reviewElement.textContent = reviews[currentIndex];
                        reviewElement.style.opacity = '1';
                    }, fadeDuration);
                }, intervalDuration);
                // Optionally store intervalId for later clearing
                reviewElement.dataset.reviewIntervalId = intervalId;
            } else {
                reviewElement.textContent = reviews[0];
            }
        }
    }, 100);
};